import { useState, useCallback, useEffect } from 'react';
import { AccountClient, GET_USERS_ASSOCIATIONS_LIST } from '../../../index';
import { COMMON_CONSTANTS } from '../../../index';
import { UserAssociation } from '../types';

interface UseUserAssociationsParams {
  client: any;
  siteId: string;
  isMSPProduct?: boolean;
  selectedDepartment?: any;
}

export const useUserAssociations = ({
  client,
  siteId,
  isMSPProduct = false,
  selectedDepartment
}: UseUserAssociationsParams) => {
  const [userAssociations, setUserAssociations] = useState<UserAssociation[]>(
    []
  );
  const [
    selectedRequestor,
    setSelectedRequestor
  ] = useState<UserAssociation | null>(null);
  const [associationsLoading, setAssociationsLoading] = useState(false);

  // Helper function to validate required fields based on product type
  const isValidationFailed = useCallback(() => {
    if (isMSPProduct) {
      // MSP: Both client?.accountId and siteId are mandatory
      return !client?.accountId || !siteId;
    } else {
      // ITSM: Only siteId is mandatory
      return !siteId;
    }
  }, [client?.accountId, siteId, isMSPProduct]);

  const fetchUserAssociations = useCallback(async () => {
    if (isValidationFailed()) return;

    setAssociationsLoading(true);
    try {
      // Build operands based on product type
      let operands = [];

      if (isMSPProduct) {
        // MSP: Use client.accountId and site.id
        operands = [
          {
            attribute: 'client.accountId',
            operator: 'is',
            value: client.accountId
          },
          {
            attribute: 'site.id',
            operator: 'is',
            value: siteId
          }
        ];
      } else {
        // ITSM: Use site.id and optionally user.businessFunction.businessFunctionId (department)
        operands = [
          {
            attribute: 'site.id',
            operator: 'is',
            value: siteId
          }
        ];

        // Add department filter if selected
        if (selectedDepartment) {
          operands.push({
            attribute: 'user.businessFunction.businessFunctionId',
            operator: 'is',
            value: selectedDepartment?.businessFunctionId
          });
        }
      }

      const response = await AccountClient.query({
        query: GET_USERS_ASSOCIATIONS_LIST,
        fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
        variables: {
          input: {
            listInfo: {
              sort: {
                attribute: 'user.name',
                order: 'ASC'
              },
              condition: {
                joinOperator: 'AND',
                operands: operands
              }
            }
          }
        },
        skip: isMSPProduct && !siteId
      });

      if (response?.data?.getClientUserAssociationsList?.associations) {
        const associations = response.data.getClientUserAssociationsList.associations.map(
          (assoc: any): UserAssociation => ({
            label: assoc.user.name,
            value: assoc.user.userId,
            user: assoc.user
          })
        );
        setUserAssociations(associations);
        // Note: Not auto-selecting any requestor since this field is optional
      }
    } catch (error) {
      //
    } finally {
      setAssociationsLoading(false);
    }
  }, [
    isValidationFailed,
    isMSPProduct,
    client?.accountId,
    siteId,
    selectedDepartment
  ]);

  useEffect(() => {
    if (!isValidationFailed()) {
      fetchUserAssociations();
    }
  }, [isValidationFailed, fetchUserAssociations]);

  // Clear selected requestor when department changes
  useEffect(() => {
    if (selectedRequestor) {
      setSelectedRequestor(null);
    }
  }, [selectedDepartment]);

  return {
    userAssociations,
    selectedRequestor,
    setSelectedRequestor,
    associationsLoading,
    fetchUserAssociations
  };
};
